import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import { useAuth } from '../../context/AuthContext';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUS_TONES,
  PARTNER_CATEGORY_LABELS,
  PARTNER_STATUS_LABELS,
  PARTNER_STATUS_TONES,
  PARTNER_TIERS,
  PARTNER_TIER_LABELS,
  PartnerTier,
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_TONES,
} from '../../constants/partnerNetwork';
import {
  ActivityEntry,
  AdminPartner,
  getPartner,
  updatePartner,
} from '../../services/partnerNetwork';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatRate,
} from '../../utils/partnerFormat';
import {
  Alert,
  Modal,
  Spinner,
  StatCard,
  StatusBadge,
  inputClass,
} from './ui';

type Referrals = Awaited<ReturnType<typeof getPartner>>['referrals'];
type Commissions = Awaited<ReturnType<typeof getPartner>>['commissions'];

const PartnerDetail: React.FC = () => {
  const { id = '' } = useParams();
  const { admin } = useAuth();

  const [partner, setPartner] = useState<AdminPartner | null>(null);
  const [referrals, setReferrals] = useState<Referrals>([]);
  const [commissions, setCommissions] = useState<Commissions>([]);
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [sourceProspect, setSourceProspect] = useState<{
    id: string;
    prospectId: string;
    fullName: string;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [rateDraft, setRateDraft] = useState('');
  const [tierDraft, setTierDraft] = useState<PartnerTier | ''>('');

  const canManage = admin?.role === 'admin' || admin?.role === 'superadmin';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPartner(id);
      setPartner(response.partner);
      setReferrals(response.referrals);
      setCommissions(response.commissions);
      setActivities(response.activities);
      setSourceProspect(response.sourceProspect);
      // Rates are stored as a fraction and edited as a percentage.
      setRateDraft(String(response.partner.commissionRate * 100));
      setTierDraft(response.partner.tier || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the partner');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async () => {
    setError(null);
    const percent = Number(rateDraft);

    if (Number.isNaN(percent) || percent < 0 || percent > 100) {
      setError('The commission rate must be between 0 and 100 percent.');
      return;
    }

    try {
      setBusy(true);
      await updatePartner(id, {
        commissionRate: percent / 100,
        tier: tierDraft || null,
      });
      setNotice('Partner settings saved.');
      setEditOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the settings');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  if (!partner) {
    return (
      <section className="py-12">
        <div className="mx-auto max-w-3xl px-4">
          <Alert tone="error">{error || 'Partner not found.'}</Alert>
          <Link to="/admin/partners/partners" className="btn-secondary mt-6 py-2.5 px-5">
            Back to partners
          </Link>
        </div>
      </section>
    );
  }

  const totalEarned = commissions.reduce((sum, c) => sum + c.commissionAmount, 0);
  const totalPaid = commissions
    .filter((c) => c.status === 'paid')
    .reduce((sum, c) => sum + c.commissionAmount, 0);

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/admin/partners/partners"
          className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 19l-7-7 7-7" />
          </svg>
          All partners
        </Link>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}
        {notice && !error && (
          <Alert tone="success" className="mb-6">
            {notice}
          </Alert>
        )}

        <AnimatedSection animation="slide-up">
          <div className="mb-6 rounded-2xl border border-surface-border bg-surface-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <h1 className="font-display text-2xl font-semibold text-white">
                    {partner.fullName}
                  </h1>
                  <StatusBadge
                    label={PARTNER_STATUS_LABELS[partner.status]}
                    tone={PARTNER_STATUS_TONES[partner.status]}
                  />
                </div>
                <p className="text-sm text-muted">
                  {[
                    partner.companyName,
                    partner.category && PARTNER_CATEGORY_LABELS[partner.category],
                    partner.tier && PARTNER_TIER_LABELS[partner.tier],
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'No company details'}
                </p>
                <p className="mt-1 font-mono text-xs text-muted/70">
                  {partner.partnerId.startsWith('PENDING-')
                    ? 'Partner ID issued on approval'
                    : partner.partnerId}
                </p>
              </div>

              {canManage && (
                <button
                  type="button"
                  onClick={() => setEditOpen(true)}
                  className="btn-secondary py-2.5 px-4 text-sm"
                >
                  Commission &amp; tier
                </button>
              )}
            </div>

            {sourceProspect && (
              <p className="mt-5 border-t border-surface-border pt-5 text-sm text-muted">
                Converted from prospect{' '}
                <Link
                  to={`/admin/partners/prospects/${sourceProspect.id}`}
                  className="text-primary-400 underline underline-offset-2"
                >
                  {sourceProspect.prospectId} — {sourceProspect.fullName}
                </Link>
                .
              </p>
            )}
          </div>
        </AnimatedSection>

        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Referrals" value={referrals.length} />
          <StatCard label="Commission rate" value={formatRate(partner.commissionRate)} />
          <StatCard label="Total earned" value={formatMoney(totalEarned)} tone="success" />
          <StatCard label="Paid out" value={formatMoney(totalPaid)} />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Referrals
              </h2>

              {referrals.length === 0 ? (
                <p className="text-sm text-muted">No referrals submitted yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-sm">
                    <thead>
                      <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-muted">
                        <th className="py-2 pr-4 font-medium">Referral</th>
                        <th className="py-2 pr-4 font-medium">Business</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                        <th className="py-2 pr-4 font-medium">Value</th>
                        <th className="py-2 font-medium">Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((referral) => (
                        <tr key={referral.id} className="border-b border-surface-border/60 last:border-0">
                          <td className="py-3 pr-4">
                            <Link
                              to={`/admin/partners/referrals/${referral.id}`}
                              className="font-mono text-xs text-primary-400 hover:underline"
                            >
                              {referral.referralId}
                            </Link>
                          </td>
                          <td className="py-3 pr-4 text-white">{referral.businessName}</td>
                          <td className="py-3 pr-4">
                            <StatusBadge
                              label={REFERRAL_STATUS_LABELS[referral.status]}
                              tone={REFERRAL_STATUS_TONES[referral.status]}
                            />
                          </td>
                          <td className="py-3 pr-4 text-muted">
                            {formatMoney(referral.projectValue)}
                          </td>
                          <td className="py-3 text-muted">{formatDate(referral.submittedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Commissions
              </h2>

              {commissions.length === 0 ? (
                <p className="text-sm text-muted">No commissions yet.</p>
              ) : (
                <ul className="space-y-2">
                  {commissions.map((commission) => (
                    <li
                      key={commission.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-surface-border bg-surface px-4 py-3"
                    >
                      <span className="text-white">
                        {formatMoney(commission.commissionAmount)}
                        {commission.paidDate && (
                          <span className="block text-xs text-muted">
                            Paid {formatDate(commission.paidDate)}
                          </span>
                        )}
                      </span>
                      <StatusBadge
                        label={COMMISSION_STATUS_LABELS[commission.status]}
                        tone={COMMISSION_STATUS_TONES[commission.status]}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Activity
              </h2>
              {activities.length === 0 ? (
                <p className="text-sm text-muted">Nothing recorded yet.</p>
              ) : (
                <ul className="space-y-3">
                  {activities.map((activity) => (
                    <li key={activity.id} className="border-l-2 border-surface-border pl-4">
                      <p className="text-sm text-white">{activity.description}</p>
                      <p className="mt-1 text-xs text-muted">
                        {activity.actorName} · {formatDateTime(activity.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-surface-border bg-surface-card p-6 lg:h-fit">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
              Contact
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-muted">Email</dt>
                <dd className="break-all text-white">{partner.email}</dd>
              </div>
              <div>
                <dt className="text-muted">Phone</dt>
                <dd className="text-white">{partner.phone || '—'}</dd>
              </div>
              <div>
                <dt className="text-muted">Location</dt>
                <dd className="text-white">
                  {[partner.city, partner.country].filter(Boolean).join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Website</dt>
                <dd className="break-all text-white">
                  {partner.website ? (
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary-400"
                    >
                      {partner.website}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Joined</dt>
                <dd className="text-white">{formatDate(partner.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-muted">Last signed in</dt>
                <dd className="text-white">{formatDate(partner.lastLoginAt)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <Modal
        open={editOpen}
        title="Commission & tier"
        description="The rate applies to referrals submitted from now on. Existing referrals keep the rate they were created with."
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              className="btn-secondary py-2.5 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveSettings}
              disabled={busy}
              className="btn-primary py-2.5 px-5 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm text-muted">Commission rate (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={rateDraft}
              onChange={(e) => setRateDraft(e.target.value)}
              className={inputClass}
            />
            <p className="mt-2 text-xs text-muted">
              Standard is 10% of the first project value.
            </p>
          </div>
          <div>
            <label className="mb-2 block text-sm text-muted">Tier</label>
            <select
              value={tierDraft}
              onChange={(e) => setTierDraft(e.target.value as PartnerTier | '')}
              className={inputClass}
            >
              <option value="">Not set</option>
              {PARTNER_TIERS.map((tier) => (
                <option key={tier} value={tier}>
                  {PARTNER_TIER_LABELS[tier]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default PartnerDetail;
